import { AffineMatrix } from "./types";

interface GCPInput {
  imageX: number;
  imageY: number;
  mapLng: number;
  mapLat: number;
}

/**
 * Compute the affine transformation matrix from pixel coordinates to map coordinates.
 *
 * Solves for 6 unknowns:
 *   lng = a*x + b*y + c
 *   lat = d*x + e*y + f
 *
 * Uses least squares when N > 3: M = (A^T A)^{-1} A^T B
 *
 * Returns null if fewer than 3 GCPs are provided or the system is singular (e.g. collinear points).
 */
export function computeAffineTransform(gcps: GCPInput[]): AffineMatrix | null {
  if (gcps.length < 3) return null;

  // Build A^T*A (3x3 symmetric) and A^T*B (3x2) directly
  // A_i = [x_i, y_i, 1], B_i = [lng_i, lat_i]
  let s_xx = 0;
  let s_xy = 0;
  let s_x = 0;
  let s_yy = 0;
  let s_y = 0;
  const n = gcps.length;

  let s_xu = 0;
  let s_xv = 0;
  let s_yu = 0;
  let s_yv = 0;
  let s_u = 0;
  let s_v = 0;

  for (const gcp of gcps) {
    const { imageX: x, imageY: y, mapLng: u, mapLat: v } = gcp;
    s_xx += x * x;
    s_xy += x * y;
    s_x += x;
    s_yy += y * y;
    s_y += y;

    s_xu += x * u;
    s_xv += x * v;
    s_yu += y * u;
    s_yv += y * v;
    s_u += u;
    s_v += v;
  }

  // A^T*A = [[s_xx, s_xy, s_x], [s_xy, s_yy, s_y], [s_x, s_y, n]]
  // Compute 3x3 inverse using cofactors and determinant

  // Cofactors
  const c00 = s_yy * n - s_y * s_y;
  const c01 = -(s_xy * n - s_y * s_x);
  const c02 = s_xy * s_y - s_yy * s_x;
  const c10 = -(s_xy * n - s_x * s_y);
  const c11 = s_xx * n - s_x * s_x;
  const c12 = -(s_xx * s_y - s_xy * s_x);
  const c20 = s_xy * s_y - s_yy * s_x;
  const c21 = -(s_xx * s_y - s_x * s_xy);
  const c22 = s_xx * s_yy - s_xy * s_xy;

  const det = s_xx * c00 + s_xy * c01 + s_x * c02;

  // Check for singular or near-singular matrix
  if (Math.abs(det) < 1e-12) return null;

  const invDet = 1 / det;

  // Inverse of A^T*A (symmetric so adjugate^T = adjugate for symmetric)
  const i00 = c00 * invDet;
  const i01 = c01 * invDet;
  const i02 = c02 * invDet;
  const i10 = c10 * invDet;
  const i11 = c11 * invDet;
  const i12 = c12 * invDet;
  const i20 = c20 * invDet;
  const i21 = c21 * invDet;
  const i22 = c22 * invDet;

  // M = inv(A^T*A) * A^T*B
  // A^T*B = [[s_xu, s_xv], [s_yu, s_yv], [s_u, s_v]]
  const a = i00 * s_xu + i01 * s_yu + i02 * s_u;
  const b = i10 * s_xu + i11 * s_yu + i12 * s_u;
  const c = i20 * s_xu + i21 * s_yu + i22 * s_u;

  const d = i00 * s_xv + i01 * s_yv + i02 * s_v;
  const e = i10 * s_xv + i11 * s_yv + i12 * s_v;
  const f = i20 * s_xv + i21 * s_yv + i22 * s_v;

  return { a, b, c, d, e, f };
}

/**
 * Transform a pixel coordinate to map coordinates using the affine matrix.
 */
export function transformPoint(matrix: AffineMatrix, px: number, py: number): [number, number] {
  return [matrix.a * px + matrix.b * py + matrix.c, matrix.d * px + matrix.e * py + matrix.f];
}

/**
 * Compute the 4 corner coordinates of the image in map space.
 * Returns in the order expected by Mapbox GL image source:
 * [top-left, top-right, bottom-right, bottom-left]
 */
export function computeImageCorners(
  matrix: AffineMatrix,
  width: number,
  height: number
): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    transformPoint(matrix, 0, 0), // top-left
    transformPoint(matrix, width, 0), // top-right
    transformPoint(matrix, width, height), // bottom-right
    transformPoint(matrix, 0, height), // bottom-left
  ];
}

/**
 * Compute the RMS residual error of the affine transformation across all GCPs.
 * Returns the root mean square of the Euclidean distances between
 * predicted and actual map coordinates.
 */
export function computeResidualError(matrix: AffineMatrix, gcps: GCPInput[]): number {
  if (gcps.length === 0) return 0;

  let sumSqError = 0;
  for (const gcp of gcps) {
    const [predLng, predLat] = transformPoint(matrix, gcp.imageX, gcp.imageY);
    const dLng = predLng - gcp.mapLng;
    const dLat = predLat - gcp.mapLat;
    sumSqError += dLng * dLng + dLat * dLat;
  }

  return Math.sqrt(sumSqError / gcps.length);
}
